import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { StarRating } from "@/components/ui/star-rating";
import {
  supplierService,
  CreateSupplierRequest,
} from "@/services/supplier";

interface CreateSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSupplierModal({
  open,
  onOpenChange,
}: CreateSupplierModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CreateSupplierRequest>({
    name: "",
    phone: "",
    email: "",
    notes: "",
    score: undefined,
  });


  const handleClose = () => {
    setFormData({ name: "", phone: "", email: "", notes: "", score: undefined });
    onOpenChange(false);
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateSupplierRequest) => supplierService.create(data),
    onSuccess: () => {
      toast.success("Proveedor registrado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error("El nombre del proveedor es requerido");
      return;
    }
    createMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar proveedor</DialogTitle>
          <DialogDescription>
            Ingresa los datos del nuevo proveedor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Proveedor Principal"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              placeholder="+573001234567"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="proveedor@example.com"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Puntuación</Label>
            <div className="flex items-center gap-3">
              <StarRating
                size="lg"
                value={formData.score ?? 0}
                onChange={(score) =>
                  setFormData((prev) => ({ ...prev, score }))
                }
              />
              <span className="text-sm text-muted-foreground">
                {formData.score ?? 0} / 10
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Información adicional sobre el proveedor..."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
